"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  type DelphiResultLabels,
  DelphiResults,
} from "@/components/tools/research-ops/delphi-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import type { QuantileMethod } from "@/lib/stats/descriptive";
import { labelsFor } from "@/lib/tool-kit/research-design/matrix";
import {
  analyzeDelphi,
  type DelphiRules,
  defaultBands,
  MAX_ROUNDS,
  parseRound,
} from "@/lib/tool-kit/research-ops/delphi";

const ROUND_ONE =
  "Panelist\tHands-on data analysis\tResearch ethics training\tReading primary papers\tStatistical software\tPreregistration\tQualitative methods\nP01\t5\t5\t4\t4\t3\t2\nP02\t4\t5\t4\t5\t4\t3\nP03\t5\t4\t3\t4\t2\t4\nP04\t5\t5\t4\t4\t3\t2\nP05\t4\t5\t5\t3\t5\t3\nP06\t5\t4\t3\t5\t2\t4\nP07\t4\t5\t4\t4\t3\t5\nP08\t5\t5\t2\t4\t4\t2\nP09\t3\t4\t4\t5\t3\t3\nP10\t5\t5\t4\t4\t2\t4\nP11\t4\t5\t3\t3\t4\t3\nP12\t5\t4\t4\t4\t3\t2";
const ROUND_TWO =
  "Panelist\tHands-on data analysis\tResearch ethics training\tReading primary papers\tStatistical software\tPreregistration\tQualitative methods\nP01\t5\t5\t4\t4\t4\t2\nP02\t4\t5\t4\t5\t4\t3\nP03\t5\t5\t4\t4\t3\t3\nP04\t5\t5\t4\t4\t4\t2\nP05\t4\t5\t5\t4\t4\t3\nP06\t5\t4\t4\t5\t3\t3\nP07\t4\t5\t4\t4\t4\t4\nP08\t5\t5\t3\t4\t4\t2\nP09\t4\t5\t4\t5\t4\t3\nP10\t5\t5\t4\t4\t3\t3\nP11\t4\t5\t4\t4\t4\t3\nP12\t5\t5\t4\t4\t4\t2";

/** English strings. A translated content file overrides these through `ui`. */
export const DELPHI_CONSENSUS_CALCULATOR_STRINGS = {
  loadExample: "Load example",
  clear: "Clear",
  scaleLegend: "Rating scale",
  scaleOption: "1 to {n}",
  bandsHeading: "Which ratings count as agreement",
  bandsHint:
    "Changing the scale resets these to common starting points. Set them to whatever your protocol states.",
  agreeLabel: "Agreement is a rating of at least",
  disagreeLabel: "Disagreement is a rating of at most",
  rulesHeading: "Consensus rules",
  rulesHint:
    "There is no single definition of consensus in Delphi studies. Switch on the rules your protocol set in advance; an item reaches consensus when every switched-on rule is met.",
  percentRule: "Percent agreement at least",
  percentUnit: "% of panelists",
  iqrRule: "Interquartile range at most",
  iqrUnit: "scale points",
  medianRule: "Median at least",
  medianUnit: "(agreement only)",
  stabilityRule: "Share changes by at most",
  stabilityUnit: "percentage points since the previous round",
  methodLabel: "Quartile method",
  methodHint:
    "Quartiles — and so the IQR — depend on the method, most of all in small panels. Report which you used.",
  methodOptions: {
    inclusive: "Inclusive (Excel QUARTILE.INC, R default)",
    exclusive: "Exclusive (Excel QUARTILE.EXC, SPSS)",
    tukey: "Median of halves (Tukey; many textbooks)",
  },
  roundsHeading: "Rounds",
  roundsHint:
    "Paste one table per round: a row per panelist and a column per item, in the same item order every round. Blank cells are missing ratings.",
  headerLabel: "First row holds item names",
  idsLabel: "First column holds panelist IDs",
  roundLabel: "Round {n} ratings",
  roundPlaceholder: "Panelist\tItem 1\tItem 2\nP01\t4\t5\nP02\t5\t4",
  addRound: "Add a round",
  removeRound: "Remove the last round",
  roundNote: "Read {panelists} and {items}.",
  panelistCount: "one: {count} panelist|other: {count} panelists",
  itemCount: "one: {count} item|other: {count} items",
  itemFallback: "Item {n}",
  problemInvalid:
    "Round {round}: {count} cells are not a rating from 1 to {scale}, so they were left out.",
  problemMismatch:
    "Round {round} has a different number of columns from the item list. Columns are matched by position.",
  problemNumbers: "Thresholds must be numbers.",
  resultsHeading: "Results",
  prompt: "Paste at least one round of ratings to see the statistics.",
  roundLegend: "Round shown",
  roundName: "Round {round}",
  roundHeading: "Round {round}",
  acrossHeading: "Median and % agreement by round",
  colItem: "Item",
  colN: "n",
  colMedian: "Median",
  colIqr: "IQR",
  colMean: "Mean",
  colAgree: "% agree",
  colDisagree: "% disagree",
  colResult: "Result",
  verdicts: {
    agree: "Consensus to agree",
    disagree: "Consensus to disagree",
    none: "No consensus",
  },
  unmetLead: "not met: {reasons}",
  unmet: {
    percent: "percent agreement",
    iqr: "IQR",
    median: "median",
    stability: "stability",
  },
  noRatings: "No ratings",
  countsHeading: "Items at consensus",
  countsLine: "Round {round}: {count} of {items}.",
  copy: "Copy for a spreadsheet",
  copied: "Copied!",
  downloadCsv: "Download CSV",
  csvFile: "delphi-consensus.csv",
  csvRound: "Round",
  note: "Consensus is computed with the rules you switched on, in the direction the ratings point. With the stability rule on, round 1 cannot show consensus because there is no earlier round to compare.",
  example: { round1: ROUND_ONE, round2: ROUND_TWO },
} as const;

const SCALES = [4, 5, 7, 9, 10] as const;
const METHODS: readonly QuantileMethod[] = ["inclusive", "exclusive", "tukey"];

/** A round's pasted table, with an id that outlives the other rounds being removed. */
interface RoundEntry {
  id: number;
  text: string;
}

interface RuleFields {
  percent: string;
  iqr: string;
  median: string;
  stability: string;
}

function numberOr(text: string, fallback: number): number {
  return parseNumberInput(text) ?? fallback;
}

export default function DelphiConsensusCalculatorWidget() {
  const strings = useToolUi(DELPHI_CONSENSUS_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [scale, setScale] = useState<number>(5);
  const [agreeFrom, setAgreeFrom] = useState("4");
  const [disagreeTo, setDisagreeTo] = useState("2");
  const [on, setOn] = useState({
    percent: true,
    iqr: true,
    median: false,
    stability: false,
  });
  const [fields, setFields] = useState<RuleFields>({
    percent: "75",
    iqr: "1",
    median: "4",
    stability: "15",
  });
  const [method, setMethod] = useState<QuantileMethod>("inclusive");
  const [header, setHeader] = useState(true);
  const [ids, setIds] = useState(true);
  const [entries, setEntries] = useState<RoundEntry[]>([{ id: 1, text: "" }]);
  const texts = entries.map((entry) => entry.text);

  const pickScale = (value: number) => {
    const bands = defaultBands(value);
    setScale(value);
    setAgreeFrom(String(bands.agreeFrom));
    setDisagreeTo(String(bands.disagreeTo));
    setFields((previous) => ({ ...previous, median: String(bands.medianMin) }));
  };
  const rules: DelphiRules = {
    scale,
    agreeFrom: parseIntegerInput(agreeFrom) ?? defaultBands(scale).agreeFrom,
    disagreeTo: parseIntegerInput(disagreeTo) ?? defaultBands(scale).disagreeTo,
    percentOn: on.percent,
    percentThreshold: numberOr(fields.percent, 75),
    iqrOn: on.iqr,
    iqrThreshold: numberOr(fields.iqr, 1),
    medianOn: on.median,
    medianMin: numberOr(fields.median, defaultBands(scale).medianMin),
    stabilityOn: on.stability,
    stabilityMax: numberOr(fields.stability, 15),
    method,
  };
  const rounds = useMemo(
    () => texts.map((text) => parseRound(text, scale, header, ids)),
    [texts, scale, header, ids]
  );
  const firstWithData = rounds.find((round) => round.ratings.length > 0);
  const width = firstWithData?.ratings[0]?.length ?? 0;
  const names = useMemo(
    () =>
      labelsFor(firstWithData?.columns ?? null, width, (n) =>
        t(strings.itemFallback, { n })
      ),
    [firstWithData, width, strings.itemFallback]
  );
  const result = analyzeDelphi(rounds, names, rules);
  const labels: DelphiResultLabels = strings;
  const setText = (id: number) => (value: string) =>
    setEntries((previous) =>
      previous.map((entry) =>
        entry.id === id ? { ...entry, text: value } : entry
      )
    );
  const addRound = () =>
    setEntries((previous) => [
      ...previous,
      { id: (previous.at(-1)?.id ?? 0) + 1, text: "" },
    ]);
  const badThreshold = (
    [
      [on.percent, fields.percent],
      [on.iqr, fields.iqr],
      [on.median, fields.median],
      [on.stability, fields.stability],
    ] as const
  ).some(([enabled, text]) => enabled && parseNumberInput(text) === null);

  const ruleRow = (key: keyof RuleFields, label: string, unit: string) => (
    <div className="flex flex-wrap items-end gap-3" key={key}>
      <div className="min-w-[14rem] pb-2">
        <Checkbox
          checked={on[key]}
          label={label}
          onChange={(checked) =>
            setOn((previous) => ({ ...previous, [key]: checked }))
          }
        />
      </div>
      <div className="w-24">
        <TextField
          id={`dl-${key}`}
          label={label}
          numeric
          onChange={(value) =>
            setFields((previous) => ({ ...previous, [key]: value }))
          }
          value={fields[key]}
        />
      </div>
      <span className={`${HINT} pb-3`}>{unit}</span>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            pickScale(5);
            setHeader(true);
            setIds(true);
            setEntries([
              { id: 1, text: strings.example.round1 },
              { id: 2, text: strings.example.round2 },
            ]);
          }}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setEntries([{ id: 1, text: "" }])}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel>
        <PillGroup
          label={strings.scaleLegend}
          name="dl-scale"
          onChange={(value) => pickScale(Number(value))}
          options={SCALES.map((n) => ({
            value: String(n),
            label: t(strings.scaleOption, { n }),
          }))}
          value={String(scale)}
        />
        <p className={HINT}>{strings.bandsHint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="dl-agree"
            label={strings.agreeLabel}
            numeric
            onChange={setAgreeFrom}
            value={agreeFrom}
          />
          <TextField
            id="dl-disagree"
            label={strings.disagreeLabel}
            numeric
            onChange={setDisagreeTo}
            value={disagreeTo}
          />
        </div>
      </Panel>

      <Panel title={strings.rulesHeading}>
        <p className={HINT}>{strings.rulesHint}</p>
        {ruleRow("percent", strings.percentRule, strings.percentUnit)}
        {ruleRow("iqr", strings.iqrRule, strings.iqrUnit)}
        {ruleRow("median", strings.medianRule, strings.medianUnit)}
        {ruleRow("stability", strings.stabilityRule, strings.stabilityUnit)}
        <SelectField
          hint={strings.methodHint}
          id="dl-method"
          label={strings.methodLabel}
          onChange={setMethod}
          options={METHODS.map((value) => ({
            value,
            label: strings.methodOptions[value],
          }))}
          value={method}
        />
      </Panel>

      <Panel title={strings.roundsHeading}>
        <p className={HINT}>{strings.roundsHint}</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={header}
            label={strings.headerLabel}
            onChange={setHeader}
          />
          <Checkbox checked={ids} label={strings.idsLabel} onChange={setIds} />
        </div>
        {entries.map((entry, index) => (
          <TextAreaField
            id={`dl-round-${entry.id}`}
            key={entry.id}
            label={t(strings.roundLabel, { n: index + 1 })}
            onChange={setText(entry.id)}
            placeholder={strings.roundPlaceholder}
            rows={6}
            value={entry.text}
          />
        ))}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={entries.length >= MAX_ROUNDS}
            onClick={addRound}
            type="button"
          >
            {strings.addRound}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={entries.length <= 1}
            onClick={() => setEntries((previous) => previous.slice(0, -1))}
            type="button"
          >
            {strings.removeRound}
          </button>
        </div>
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {badThreshold ? (
          <ErrorNotice>{strings.problemNumbers}</ErrorNotice>
        ) : null}
        {rounds.map((round, index) =>
          round.invalid > 0 ? (
            <ErrorNotice key={`invalid-${index + 1}`}>
              {t(strings.problemInvalid, {
                round: fmt(index + 1),
                count: fmt(round.invalid),
                scale: fmt(scale),
              })}
            </ErrorNotice>
          ) : null
        )}
        {result.mismatched.map((round) => (
          <ErrorNotice key={`mismatch-${round}`}>
            {t(strings.problemMismatch, { round: fmt(round) })}
          </ErrorNotice>
        ))}
        {result.roundCount === 0 ? (
          <Notice>{strings.prompt}</Notice>
        ) : (
          <>
            <p className={HINT}>
              {t(strings.roundNote, {
                panelists: tPlural(
                  strings.panelistCount,
                  locale,
                  firstWithData?.ratings.length ?? 0,
                  { count: fmt(firstWithData?.ratings.length ?? 0) }
                ),
                items: tPlural(strings.itemCount, locale, names.length, {
                  count: fmt(names.length),
                }),
              })}
            </p>
            <DelphiResults labels={labels} result={result} />
          </>
        )}
      </section>
    </div>
  );
}
